import APIConfig from 'common/services/ApiConfig';
import type {
  MerchantInfoInterface,
  responseInterface,
  whitelistAutomaticDiscountsInterface,
} from 'common/services/Interfaces';
import { store_errScreenMessage } from 'common/stores/CommonStore';
import * as textConstants from '../../common/utils/TextConstants';
import { appCallback, logEvent, shopifyRedirectEvent } from './Event.service';
import { getCall } from './Api.service';

let environment = window.merchantInfo?.environment || '',
  merchantInfo: MerchantInfoInterface = window.merchantInfo || {};

const EMAIL_ENCRYPTION_IV = '2134765890213456',
  EMAIL_ENCRYPTION_KEY = 'kR7dmP9tnL2wjH5&vC8qxF4hwQ1iyT6t';

export const checkIfAndroid = () => {
  const userAgent = window.navigator.userAgent;
  let isAndroid = /Android/.test(userAgent);
  [
    'crios',
    'insta',
    'wv',
    'facebook',
    'fb',
    'FBAN',
    'FBAV',
    'FB_IAB',
    'Firefox',
    'GSA',
    'UCBrowser',
    'Jasmine',
    'OPR',
    'Opera Mobi',
    'OPT',
  ].forEach((ua) => {
    if (userAgent.includes(ua)) {
      isAndroid = false;
    }
  });
  return isAndroid;
};

export function getGkCookieArray(cname: string): string[] {
  let name = cname + '=';
  let decodedCookie = document.cookie;
  let ca = decodedCookie.split(';');
  let cookies: string[] = [];
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim();
    if (c.startsWith(name)) {
      cookies.push(c.substring(name.length));
    }
  }
  return cookies;
}

export function setCookie(name, value, days) {
  // Calculate the cookie expiration date
  var expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + days);
  // Determine the domain using the getPath function
  // Create the cookie string with SameSite=None and the dynamically determined domain
  var cookieString =
    name +
    '=' +
    encodeURIComponent(value) +
    '; path=/; expires=' +
    expirationDate.toUTCString() +
    '; SameSite=None; Secure; domain=';
  // Set the cookie
  document.cookie = cookieString;
}

export function getCookie(name) {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return decodeURIComponent(cookieValue);
    }
  }
  return null; // Return null if the cookie is not found
}

export function deleteCookie(name) {
  // Set the expiration date to a past date to delete the cookie
  var expirationDate = new Date();
  expirationDate.setFullYear(expirationDate.getFullYear() - 1);

  // Determine the domain using the getPath function
  var domain = getEnvPath(environment);

  // Create the cookie string to delete the cookie
  var cookieString =
    name + '=; path=/; expires=' + expirationDate.toUTCString() + '; SameSite=None; Secure; domain=' + domain; // Include the domain if it's not empty

  // Set the cookie with the past expiration date to delete it
  document.cookie = cookieString;
}

export const checkIfGlanceEnvironment = () => {
  try {
    if (window?.GlanceAndroidInterface) {
      return true;
    }
    return false;
  } catch (err) {
    return false;
  }
};

export const checkMerchantHealth = async (mid: string, appFlow = null, isCustomStoreFront = null, optionalArg = {}) => {
  try {
    const res: responseInterface = await getCall(
      `${APIConfig.healthService}?merchantId=${mid}`,
      {},
      false,
      undefined,
      optionalArg
    );
    if (res?.data?.[0]?.is_checkout_healthy === false) {
      logEvent(
        {
          name: 'merchant_unhealthy',
          eventType: 'development',
          data: {
            health_flag: res?.data?.[0]?.is_checkout_healthy,
            unhealthy_reason: res?.data?.[0]?.unhealthy_reason,
          },
        },
        false,
        optionalArg
      );
      if (isCustomStoreFront) {
        shopifyRedirectEvent();
      } else triggerCheckoutRedirect('checkoutHealth', appFlow, optionalArg);
    }
  } catch (error) {
    console.log('error fetching merchant health: ', error);
  }
};

const triggerCheckoutRedirect = (type: string, appFlow = null, optionalArg = {}) => {
  store_errScreenMessage.set(textConstants.healthService.userFacingMessage);
  let origin: string = `${getCheckoutUrl()}/cart/checkout?`;
  const merchantUrl = getMerchantUrl();
  const url: string = `${origin}checkout`;
  window.parent.postMessage({ type: 'shopifyRedirect', link: origin }, merchantUrl);
  type === 'shopifyRedirect' &&
    logEvent({ name: 'payViaNetbanking', type: 'event', eventType: 'click' }, false, optionalArg);
  if (!merchantInfo?.appFlow && !appFlow) {
    appCallback({ name: 'openInBrowserTab', url: url });
    window.parent.location.replace(url);
  } else if (merchantInfo?.appFlow && !appFlow) {
    appCallback({ name: 'openInBrowserTab', url: url });
  }
};

function getEnvPath(env) {
  return env === 'local'
    ? ''
    : env === 'dev' || env === 'hdev' || env === 'ndev' || env === 'idev'
      ? 'dev.pdp.gokwik.co'
      : env === 'production'
        ? 'pdp.gokwik.co'
        : env === 'sandbox'
          ? 'sandbox.pdp.gokwik.co'
          : env === 'qa-rto'
            ? 'qa2.pdp.gokwik.co'
            : env === 'qaone'
              ? 'qa1.pdp.gokwik.co'
              : env === 'qatwo' || env === 'idev'
                ? 'qatwo.pdp.gokwik.co'
                : env === 'qa'
                  ? 'qa.pdp.gokwik.co'
                  : ''; // Return an empty string for unknown environments
}

export function getPath(env: string) {
  return env === 'local'
    ? 'http://localhost:8080'
    : env === 'dev' || env === 'hdev' || env === 'ndev'
      ? 'https://dev.pdp.gokwik.co'
      : env === 'production' || env === 'pci-prod'
        ? 'https://pdp.gokwik.co'
        : env === 'sandbox'
          ? 'https://sandbox.pdp.gokwik.co'
          : env === 'qa-rto'
            ? 'https://qa2.pdp.gokwik.co'
            : env === 'qaone'
              ? 'https://qa1.pdp.gokwik.co'
              : env === 'qatwo' || env === 'idev'
                ? 'https://qatwo.pdp.gokwik.co'
                : env === 'qa' && 'https://qa.pdp.gokwik.co';
}

const getCheckoutUrl = (): string => {
  const merchantUrl = getMerchantUrl();
  return new URL(merchantUrl).origin;
};

const getMerchantUrl = (): string => {
  return `${window.location.protocol}//${window.location.host}`;
};

export const checkIfEmailBuggy = (email) => {
  if (!email) {
    email = '';
  }
  email = email.trim();
  let isEmailBuggy = false;
  textConstants.buggyEmailList.buggyEmailListContains.forEach((i) => {
    if (email.toLowerCase().includes(i)) {
      isEmailBuggy = true;
    }
  });
  textConstants.buggyEmailList.buggyEmailListExactMatch.forEach((i) => {
    if (email.toLowerCase() === i) {
      isEmailBuggy = true;
    }
  });
  return isEmailBuggy;
};

export const getWigzoDataBody = (data) => {
  return {
    eventType: 'track',
    eventName: 'checkoutstarted',
    data: {
      id: data.request_id,
      token: null,
      cart_token: data.cart.token,
      email: data.userAddress?.email,
      gateway: null,
      buyer_accepts_marketing: true,
      created_at: null,
      updated_at: null,
      landing_site: null,
      note: null,
      note_attributes: [],
      referring_site: null,
      shipping_lines: [],
      taxes_included: false,
      total_weight: null,
      currency: data.cart.currency,
      completed_at: null,
      closed_at: null,
      user_id: null,
      location_id: null,
      source_identifier: null,
      source_url: null,
      device_id: null,
      phone: `+91${data.phoneNumber}`,
      customer_locale: null,
      line_items: [
        data.cart.items.map(
          (wigzoItem) =>
            `{ "applied_discounts": [],
                    "discount_allocations": [],
                    "key": ${wigzoItem?.key},
                    "destination_location_id": null,
                    "fulfillment_service": null,
                    "gift_card": ${wigzoItem?.gift_card},
                    "grams": ${wigzoItem?.grams},
                    "origin_location_id": null, "presentment_title": ${wigzoItem?.product_title}, "presentment_variant_title": ${wigzoItem?.title},
                    "product_id": ${wigzoItem?.product_id},
                    "properties": null,
                    "quantity": ${wigzoItem?.quantity},
                    "requires_shipping": ${wigzoItem?.requires_shipping},
                    "sku": ${wigzoItem?.sku},
                    "tax_lines": [
                    ],
                    "taxable": ${wigzoItem?.taxable},
                    "title": ${wigzoItem?.title},
                    "variant_id": ${wigzoItem?.variant_id},
                    "variant_title": ${wigzoItem?.variant_title},
                    "variant_price": ${wigzoItem?.variant_price},
                    "vendor": "${wigzoItem?.vendor}",
                    "user_id": null,
                    "unit_price_measurement": null,
                    "rank": null,
                    "compare_at_price": null,
                    "line_price": ${wigzoItem?.final_line_price},
                    "price": ${wigzoItem?.final_price}
                    },`
        ),
      ],
      name: null,
      source: null,
      Abandoned_Cart: `${data.merchantUrl}?mrid=${data.request_id}`,
      ABC_sms: `${data.merchantUrl}?mrid=${data.request_id}&ch=wigzo&src=sms`,
      ABC_mail: `${data.merchantUrl}?mrid=${data.request_id}&ch=wigzo&src=mail`,
      ABC_wa: `${data.merchantUrl}?mrid=${data.request_id}&ch=wigzo&src=wa`,
      ABC_limechat: `${data.merchantUrl}?mrid=${data.request_id}&ch=wigzo&src=limechat`,
      ABC_gupshup: `${data.merchantUrl}?mrid=${data.request_id}&ch=wigzo&src=gupshup`,
      ABC_zoko: `${data.merchantUrl}?mrid=${data.request_id}&ch=wigzo&src=zoko`,
      discount_codes: [],
      tax_lines: [],

      source_name: null,
      presentment_currency: 'INR',
      total_discounts: null,
      total_line_items_price: null,
      total_price: null,

      total_tax: null,
      subtotal_price: null,
      total_duties: null,
      billing_address: {
        first_name: data.evt.userAddress?.firstname,
        address1: data.evt.userAddress?.address1,
        phone: `+91${data.phoneNumber}`,
        city: data.evt.userAddress?.city,
        zip: data.evt.userAddress?.pincode,
        province: data.evt.userAddress?.state,
        country: data.evt.userAddress?.country,
        last_name: data.evt.userAddress?.lastname,
        address2: data.evt.userAddress?.address2,
        company: 'My Company',
        latitude: null,
        longitude: null,
        name: null,
        country_code: 'IN',
        province_code: data.evt.userAddress?.pincode,
      },

      shipping_address: {
        first_name: data.evt.userAddress?.firstname,
        address1: data.evt.userAddress?.address1,
        phone: `+91${data.phoneNumber}`,
        city: data.evt.userAddress?.city,
        zip: data.evt.userAddress?.pincode,
        province: data.evt.userAddress?.state,
        country: data.evt.userAddress?.country,
        last_name: data.evt.userAddress?.lastname,
        address2: data.evt.userAddress?.address2,
        company: 'My Company',
        latitude: null,
        longitude: null,
        name: null,
        country_code: 'IN',
        province_code: data.evt.userAddress?.pincode,
      },
      customer: {
        id: data.evt.userAddress?.customer_id,
        email: data.evt.userAddress?.email,
        accepts_marketing: null,
        created_at: data.evt.userAddress?.created_at,
        updated_at: data.evt.userAddress?.updated_at,
        first_name: data.evt.userAddress?.firstname,
        last_name: data.evt.userAddress?.lastname,
        orders_count: null,
        state: data.evt.userAddress?.state,
        total_spent: null,
        last_order_id: null,
        note: null,
        verified_email: null,
        multipass_identifier: null,
        tax_exempt: null,
        phone: `+91${data.phoneNumber}`,
        tags: null,
        last_order_name: null,
        currency: 'IN',
        accepts_marketing_updated_at: null,
        marketing_opt_in_level: null,
        admin_graphql_api_id: null,
        default_address: {
          id: null,
          customer_id: null,
          first_name: null,
          last_name: null,
          company: null,
          address1: null,
          address2: null,
          city: null,
          province: null,
          country: null,
          zip: null,
          phone: null,
          name: null,
          province_code: null,
          country_code: null,
          country_name: null,
          default: null,
        },
      },
    },
  };
};

export function xhrPostCall(url, body, headers?) {
  return new Promise(function (resolve) {
    let xhr = new XMLHttpRequest();
    // xhr.responseType = 'json';
    xhr.open('POST', url);
    initializeXhrHeaders(xhr, headers);
    xhr.onload = function () {
      const response = JSON.parse(xhr.response);
      resolve(response);
    };
    xhr.onerror = function () {
      const response = JSON.parse(xhr.response);
      resolve(response);
    };
    xhr.send(JSON.stringify(body));
  });
}
export function initializeXhrHeaders(xhr, headers = {}) {
  xhr.setRequestHeader('Content-Type', 'application/json');
  for (const [key, value] of Object.entries(headers)) {
    xhr.setRequestHeader(key, value);
  }
}

export function phoneFormatter(phone, obj) {
  if (!obj?.phone_format || !phone) return phone;
  return `${obj.phone_format}${phone}`;
}

export function checkWhitelistedAutomaticDiscount(
  discountCode: string,
  whitelisted: whitelistAutomaticDiscountsInterface[]
): whitelistAutomaticDiscountsInterface | null {
  if (!discountCode) return null;
  const code = discountCode.toLowerCase();
  const found = whitelisted.find((discount) => {
    const target = discount.automatic_code.toLowerCase();
    switch (discount.automatic_code_match_type) {
      case 1:
        return code === target;
      case 2:
        return code.startsWith(target);
      case 3:
        return code.endsWith(target);
      case 4:
        return code.includes(target);
      default:
        return false;
    }
  });

  return found ?? null;
}

export const getIframeUrl = (env) => {
  return env === 'local'
    ? 'http://localhost:8080'
    : env === 'dev' || env === 'hdev' || env === 'ndev' || env === 'idev'
      ? 'https://dev.pdp.gokwik.co'
      : env === 'production'
        ? 'https://pdp.gokwik.co'
        : env === 'sandbox'
          ? 'https://sandbox.pdp.gokwik.co'
          : env === 'qa-rto'
            ? 'https://qa2.pdp.gokwik.co'
            : env === 'qaone'
              ? 'https://qa1.pdp.gokwik.co'
              : env === 'qatwo' || env === 'idev'
                ? 'https://qatwo.pdp.gokwik.co'
                : env === 'qa' && 'https://qa.pdp.gokwik.co';
};

const enc = new TextEncoder();
const dec = new TextDecoder();

async function importKey(keyString: string): Promise<CryptoKey> {
  const rawKey = enc.encode(keyString);
  return crypto.subtle.importKey('raw', rawKey, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt']);
}

export async function decryptEmail(encryptedBase64: string): Promise<string> {
  const key = await importKey(EMAIL_ENCRYPTION_KEY);
  const iv = enc.encode(EMAIL_ENCRYPTION_IV);

  const encryptedBytes = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0));

  const decrypted = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, encryptedBytes);

  return dec.decode(decrypted);
}

export function isBase64(str: string) {
  const notBase64 = /[^A-Z0-9+\/=]/i;
  if (!str || str.length % 4 !== 0 || notBase64.test(str)) return false;

  try {
    return btoa(atob(str)) === str;
  } catch (err) {
    return false;
  }
}
